import {Accordion} from '@/components/molecules/Accordion'
import {AccordionItem} from '@/components/molecules/AccordionItem'
import {DataTable, ButtonWithIcon} from '@/components/atoms'
import {Font} from 'pepsico-ds'
import {useMemo} from 'react'
import {TabsComponent} from '@/components/molecules'
import {useIntl} from 'react-intl'
import {SIZE} from '@/utils/constants/globalStyle'
import {COLOR} from '@/utils/constants/colors'
import {useFormContext} from 'react-hook-form'
import './_review.scss'
import {getCachedImageDataUrl, getCachedImageName} from '@/services/tierImageService'
import ImageViewer from '@/components/atoms/ImageViewer'

const columns = [
  {columnKey: 'activityName', header: 'ACTIVITY'},
  {columnKey: 'multiplier', header: 'POINT MULTIPLIER', cellData: ctx => <span>{ctx.row.original.multiplier}x</span>},
]

const DisplayContent = ({language}) => {
  const {formatMessage} = useIntl()
  const {getValues} = useFormContext()

  const content = getValues(`content.${language}`)

  if (!content) {
    return (
      <div className="p-4 text-gray-400">
        <p>{formatMessage({id: 'CLUB_PLUS.TIER_CREATE_NO_CONTENT'})}</p>
      </div>
    )
  }

  return (
    <>
      <div className="my-5">
        <Font variant="bold" size="xsmall" textColorClass="text-white">
          {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_HOMEPAGE_TITLE'})}
        </Font>
      </div>
      <div className="flex justify-between align-top">
        <div className="flex w-60 gap-4">
          <ImageViewer url={getCachedImageDataUrl(content?.imageTierIcon) || ''} />
          <div>
            <div className="review_subtitle">
              {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_TIER_ICON_LABEL'})}
              <div>
                <span style={{fontWeight: 'bold'}}>{getCachedImageName(content?.imageTierIcon)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="w-60">
          <div className="review_subtitle">{formatMessage({id: 'CLUB_PLUS.TIER_CREATE_REVIEW_DISPLAY_NAME'})}</div>
          <div className="review_title">
            <span>{content?.tierName}</span>
          </div>
        </div>
        <div className="w-60">
          <div className="review_subtitle">{formatMessage({id: 'CLUB_PLUS.TIER_CREATE_REVIEW_DESCRIPTION_LABEL'})}</div>
          <div className="review_title">
            <span>{content?.displayDescription || '-'}</span>
          </div>
        </div>
      </div>

      <div className="my-5">
        <Font variant="bold" size="xsmall" textColorClass="text-white">
          {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_DETAIL_BANNER'})}
        </Font>
      </div>
      <div className="flex justify-between align-top">
        <div className="flex w-60 gap-4">
          <ImageViewer url={getCachedImageDataUrl(content?.bannerImage) || ''} />
          <div>
            <div className="review_subtitle">
              {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_REVIEW_HOMEPAGE_IMAGE'})}
              <div>
                <span style={{fontWeight: 'bold'}}>{getCachedImageName(content?.bannerImage)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="w-60">
          <div className="review_subtitle">
            {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_REVIEW_BANNER_HEADER_LABEL'})}
          </div>
          <div className="review_title">
            <span>{content?.bannerHeader}</span>
          </div>
        </div>
        <div className="w-60">
          <div className="review_subtitle">{formatMessage({id: 'CLUB_PLUS.TIER_CREATE_REVIEW_DESCRIPTION_LABEL'})}</div>
          <div className="review_title">
            <span>{content?.bannerDescription || '-'}</span>
          </div>
        </div>
      </div>

      <div className="my-5">
        <Font variant="bold" size="xsmall" textColorClass="text-white">
          {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_DETAIL_PAGE'})}
        </Font>
      </div>
      <div className="gap- flex justify-between align-top">
        <div className="flex w-60 gap-4">
          <ImageViewer url={getCachedImageDataUrl(content?.detailsImage) || ''} />
          <div>
            <div className="review_subtitle">
              {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_REVIEW_DETAIL_PAGE_IMG'})}
              <div>
                <span style={{fontWeight: 'bold'}}>{getCachedImageName(content?.detailsImage)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="w-60">
          <div className="review_subtitle">{formatMessage({id: 'CLUB_PLUS.TIER_CREATE_REVIEW_DESCRIPTION_LABEL'})}</div>
          <div className="review_title">
            <span>{content?.detailsDescription}</span>
          </div>
        </div>
        <div className="w-60">
          <div className="review_subtitle">
            {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_FORM_TERMS_CONDITIONS_LABEL'})}
          </div>
          <div className="review_title">
            <span>{content?.detailsTermsAndConditions || '-'}</span>
          </div>
        </div>
      </div>
    </>
  )
}

export const Review = ({setPhase, isEditMode = true, onEdit}) => {
  const {formatMessage} = useIntl()
  const {getValues} = useFormContext()

  const renderEditButton = phaseNumber => (
    <ButtonWithIcon
      uiSchema={{
        className: 'ml-8 flex items-center text-sm text-[#0069D7] border-none',
        title: 'Edit',
        size: SIZE.SMALL,
        variant: COLOR.TERTIARY,
        isDark: true,
      }}
      iconLeading="edit"
      handleButton={e => {
        e?.stopPropagation()
        if (isEditMode) {
          setPhase(phaseNumber)
        } else {
          if (onEdit) onEdit(phaseNumber)
        }
      }}
    />
  )

  const tabLangs = useMemo(
    () => [
      {
        id: formatMessage({id: 'LANGUAGES.ENGLISH_ID'}),
        tabLabel: formatMessage({id: 'LANGUAGES.ENGLISH_LABEL'}),
        title: formatMessage({id: 'LANGUAGES.ENGLISH_LABEL'}),
        tabComponent: <DisplayContent language={formatMessage({id: 'LANGUAGES.ENGLISH_ID'})} />,
      },
      {
        id: formatMessage({id: 'LANGUAGES.SPANISH_MEXICO_ID'}),
        tabLabel: formatMessage({id: 'LANGUAGES.SPANISH_MEXICO_LABEL'}),
        title: formatMessage({id: 'LANGUAGES.SPANISH_MEXICO_LABEL'}),
        tabComponent: <DisplayContent language={formatMessage({id: 'LANGUAGES.SPANISH_MEXICO_ID'})} />,
      },
    ],
    [formatMessage]
  )

  return (
    <div className="create-tier-review mt-16 flex flex-col gap-6">
      <Accordion
        isDark
        size="large"
        presentation="border"
        separator
        content={
          <>
            <AccordionItem
              isDark
              expanded
              label="Tier Setup"
              size="small"
              rightAccessoryChild={renderEditButton(1)}
              content={
                <div>
                  <Font variant="regular" size="xsmall" textColorClass="text-white">
                    Tier
                  </Font>
                  <div>
                    <Font variant="regular" size="body" textColorClass="text-white">
                      {getValues('name')}
                    </Font>
                  </div>
                  <div className="mt-5 w-full">
                    <div className="mt-2">
                      <Font variant="bold" size="body" textColorClass="text-white">
                        {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_SETUP_ACTIVITIES'})}
                      </Font>
                    </div>
                  </div>
                  <div className="tier-review-table">
                    {(getValues('activities') || []).length > 0 ? (
                      <DataTable
                        columns={columns}
                        data={getValues('activities') || []}
                        isDark
                        selection="single"
                        showFilterButton={false}
                        showColumnFilters={false}
                        showFilterToolbar={false}
                        showPagination={false}
                        manualPagination={false}
                        enableSorting={false}
                        showGlobalActions={false}
                      />
                    ) : (
                      <div className="rounded border border-gray-600 p-4 text-gray-400">
                        <p>{formatMessage({id: 'CLUB_PLUS.TIER_CREATE_NO_ACTIVITIES'})}</p>
                      </div>
                    )}
                  </div>
                </div>
              }
            />
          </>
        }
      />
      <Accordion
        isDark
        size="large"
        presentation="border"
        separator
        content={
          <>
            <AccordionItem
              isDark
              expanded
              label="Display Content"
              size="small"
              rightAccessoryChild={renderEditButton(2)}
              content={
                <div className="mt-2 w-full">
                  <div className="mb-3">
                    <Font headingLevel={5} size="xsmall" textColorClass="text-white">
                      {formatMessage({id: 'CLUB_PLUS.TIER_CREATE_HOMEPAGE_PREVIEW'})}
                    </Font>
                  </div>
                  <TabsComponent tabList={tabLangs} />
                </div>
              }
            />
          </>
        }
      />
    </div>
  )
}
